import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCircle2, CalendarHeart, Loader2 } from "lucide-react";
import {
  getAvailabilityRequest,
  submitAvailabilityResponse,
} from "@/lib/therapist-events.functions";

export const Route = createFileRoute("/therapist-availability/$token")({
  head: () => ({
    meta: [
      { title: "Submit Availability — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AvailabilityPage,
});

const CATEGORIES = [
  "Trauma Recovery",
  "CPTSD",
  "PTSD",
  "Nervous System Regulation",
  "Somatic Exercises",
  "EMDR",
  "Art Therapy",
  "Grief",
  "Relationships",
  "Self-Worth",
  "Other",
];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const EVENT_TYPES = [
  "Workshop",
  "Live Q&A",
  "Support Group",
  "Webinar",
  "Guest Speaker",
  "Meditation",
  "Yoga",
  "Other",
];

function AvailabilityPage() {
  const { token } = Route.useParams();
  const lookup = useServerFn(getAvailabilityRequest);
  const submit = useServerFn(submitAvailabilityResponse);

  const { data, isLoading, error } = useQuery({
    queryKey: ["availability-request", token],
    queryFn: () => lookup({ data: { token } }),
  });

  const [form, setForm] = useState({
    session_title: "",
    workshop_description: "",
    category: "",
    time_zone: "",
    days_available: [] as string[],
    preferred_time: "",
    event_types: [] as string[],
    allow_recording: false,
  });

  const mutation = useMutation({
    mutationFn: () => submit({ data: { token, ...form } }),
  });

  if (isLoading) {
    return (
      <div className="mx-auto flex max-w-xl items-center justify-center px-4 py-20 text-muted-foreground">
        <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (error || !data?.found) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-serif text-2xl text-foreground">Link not valid</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This availability request link is invalid or has expired. Please
          contact{" "}
          <a className="text-[var(--gold-deep)] underline" href="mailto:therapists@recoverytrauma.com">
            therapists@recoverytrauma.com
          </a>
          .
        </p>
      </div>
    );
  }

  if (data.status === "responded" || mutation.isSuccess) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <h1 className="font-serif text-2xl text-foreground">Thank you</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your availability has been submitted to the Recovery Trauma™ team.
          We will be in touch shortly.
        </p>
      </div>
    );
  }

  const toggle = (key: "days_available" | "event_types", v: string) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v],
    }));
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
          <CalendarHeart className="h-5 w-5" />
        </div>
        <h1 className="font-serif text-3xl text-foreground">Submit Availability</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Hello {data.therapist_name as string} — thank you for being part of
          Recovery Trauma™. Please share your availability and the sessions
          you'd like to offer.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-8"
      >
        {/* Session Information */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-serif text-lg text-foreground">Session Information</h2>
          <div className="mt-4 space-y-4">
            <Field label="Session Title">
              <input
                className="rt-input"
                value={form.session_title}
                onChange={(e) => setForm({ ...form, session_title: e.target.value })}
                maxLength={200}
              />
            </Field>
            <Field label="Workshop Description">
              <textarea
                className="rt-input min-h-[120px]"
                value={form.workshop_description}
                onChange={(e) => setForm({ ...form, workshop_description: e.target.value })}
                maxLength={4000}
              />
            </Field>
            <Field label="Category">
              <select
                className="rt-input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="">Select category…</option>
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        {/* Availability */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-serif text-lg text-foreground">Availability</h2>
          <div className="mt-4 space-y-4">
            <Field label="Time Zone">
              <input
                className="rt-input"
                placeholder="e.g. GMT, EST, PST"
                value={form.time_zone}
                onChange={(e) => setForm({ ...form, time_zone: e.target.value })}
                maxLength={120}
              />
            </Field>
            <div>
              <p className="text-sm font-medium text-foreground">Days Available</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <label
                    key={d}
                    className={`cursor-pointer rounded-full border px-3 py-1.5 text-sm ${
                      form.days_available.includes(d)
                        ? "border-[var(--gold-deep)] bg-gold/15 text-foreground"
                        : "border-border bg-background text-muted-foreground"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.days_available.includes(d)}
                      onChange={() => toggle("days_available", d)}
                    />
                    {d}
                  </label>
                ))}
              </div>
            </div>
            <Field label="Preferred Time">
              <input
                className="rt-input"
                placeholder="e.g. evenings UK time"
                value={form.preferred_time}
                onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                maxLength={200}
              />
            </Field>
          </div>
        </section>

        {/* Event Type */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-serif text-lg text-foreground">Event Type</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {EVENT_TYPES.map((t) => (
              <label
                key={t}
                className={`cursor-pointer rounded-full border px-3 py-1.5 text-sm ${
                  form.event_types.includes(t)
                    ? "border-[var(--gold-deep)] bg-gold/15 text-foreground"
                    : "border-border bg-background text-muted-foreground"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={form.event_types.includes(t)}
                  onChange={() => toggle("event_types", t)}
                />
                {t}
              </label>
            ))}
          </div>
        </section>

        {/* Recording */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-serif text-lg text-foreground">Recording Permission</h2>
          <div className="mt-3 space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="rec"
                checked={form.allow_recording === true}
                onChange={() => setForm({ ...form, allow_recording: true })}
              />
              Allow Replay Recording
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="rec"
                checked={form.allow_recording === false}
                onChange={() => setForm({ ...form, allow_recording: false })}
              />
              Live Only
            </label>
          </div>
        </section>

        {mutation.error && (
          <p className="text-sm text-red-600">
            {(mutation.error as Error).message}
          </p>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-full bg-[var(--gold-deep)] px-5 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
        >
          {mutation.isPending ? "Submitting…" : "Submit Availability"}
        </button>
      </form>

      <style>{`.rt-input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;color:hsl(var(--foreground));}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}
